import { useMemo } from 'react';
import type { FigureCompositionGrid } from '@/store/figure-composition';

interface GridOverlayProps {
  grid: FigureCompositionGrid;
  rowHeight: number;
  containerWidth: number;
  containerHeight: number;
  enabled: boolean;
  padding?: number; // Padding in pixels (default 16px for p-4)
}

export function GridOverlay({
  grid,
  rowHeight,
  containerWidth,
  containerHeight,
  enabled,
  padding = 16, // p-4 = 16px
}: GridOverlayProps) {
  const gridLines = useMemo(() => {
    if (!enabled || containerWidth === 0 || containerHeight === 0) {
      return { vertical: [], horizontal: [] };
    }

    // Account for padding - grid area is inside padding
    const gridAreaWidth = containerWidth - (padding * 2);
    const gridAreaHeight = containerHeight - (padding * 2);
    
    // Calculate grid unit width based on columns
    const gridUnitWidth = gridAreaWidth / grid.cols;
    const verticalLines: number[] = [];
    const horizontalLines: number[] = [];

    // Vertical lines (columns) - start from padding offset
    for (let i = 0; i <= grid.cols; i++) {
      verticalLines.push(padding + (i * gridUnitWidth));
    }

    // Horizontal lines (rows) - start from padding offset
    const rowCount = Math.ceil(gridAreaHeight / rowHeight);
    for (let i = 0; i <= rowCount; i++) {
      horizontalLines.push(padding + (i * rowHeight));
    }

    return { vertical: verticalLines, horizontal: horizontalLines };
  }, [enabled, grid.cols, rowHeight, containerWidth, containerHeight, padding]);

  if (!enabled) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      {/* Vertical grid lines */}
      {gridLines.vertical.map((x, i) => (
        <div
          key={`v-${i}`}
          className="absolute top-0 bottom-0 w-px bg-gray-300/40"
          style={{ left: `${x}px` }}
        />
      ))}
      {/* Horizontal grid lines */}
      {gridLines.horizontal.map((y, i) => (
        <div
          key={`h-${i}`}
          className="absolute left-0 right-0 h-px bg-gray-300/40"
          style={{ top: `${y}px` }}
        />
      ))}
    </div>
  );
}

